import React from 'react';
import { MapPin, Trees, BookOpen, Users } from 'lucide-react';
import { Card, CardContent } from './ui/card';

export const CampusVision = () => {
  const courtyards = [
    {
      name: 'Jane Austen Courtyard',
      image: process.env.REACT_APP_JANE_AUSTEN_COURTYARD_URL,
      description: 'Elegant landscaping with pink roses and formal gardens creates a peaceful space for reading, reflection, and literary discussions.',
      purpose: 'Perfect for English Language Arts activities and quiet study',
      color: 'tealAccent'
    },
    {
      name: 'Shakespeare Courtyard',
      image: process.env.REACT_APP_SHAKESPEARE_COURTYARD_URL,
      description: 'Two-story arched colonnade with white flowering gardens and central fountain.',
      purpose: 'An ideal setting for dramatic performances, public speaking practice, and creative expression',
      color: 'goldenOlive'
    },
    {
      name: 'Founders Courtyard',
      image: process.env.REACT_APP_FOUNDERS_COURTYARD_URL,
      description: 'Vibrant garden spaces with colorful flowers and formal pathways.',
      purpose: 'Designed for social studies projects, civic engagement activities, and collaborative learning',
      color: 'sageGreen'
    },
    {
      name: 'Mozart Courtyard',
      image: process.env.REACT_APP_MOZART_COURTYARD_URL,
      description: 'Bright, cheerful space with multicolored blooms and open lawn areas.',
      purpose: 'Perfect for music performances, arts integration, and creative movement activities',
      color: 'coralRed'
    },
    {
      name: 'Malala Courtyard',
      image: process.env.REACT_APP_MALALA_COURTYARD_URL,
      description: 'Serene environment with herringbone brick pathways and natural landscaping.',
      purpose: 'Dedicated to student leadership development, global awareness, and service learning projects',
      color: 'navyBlue'
    }
  ];

  return (
    <section id="campus" className="py-20 px-4 sm:px-6 lg:px-8 bg-navyBlue/5">
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-block bg-goldenOlive/10 px-6 py-2 rounded-full mb-4">
            <span className="text-goldenOlive font-semibold">Our Campus</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-navyBlue mb-6">A Space Designed for Growth</h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Thoughtfully designed campus with Spanish Colonial architecture creating inspiring learning environments where middle school students thrive
          </p>
        </div>

        {/* Concept Art Section */}
        <div className="mb-16">
          <div className="text-center mb-8">
            <h3 className="text-3xl font-bold text-navyBlue mb-3">Campus Concept Art</h3>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              Architectural renderings showcasing our vision for Doctrina Charter School's beautiful Spanish Colonial campus
            </p>
          </div>
          
          {/* Facade Concept */}
          <div className="relative rounded-3xl overflow-hidden shadow-2xl mb-8 max-h-[400px]">
            <img 
              src={process.env.REACT_APP_CONCEPT_FACADE_URL} 
              alt="Doctrina Charter School Proposed Facade" 
              className="w-full h-full object-cover"
              style={{maxHeight: '400px'}}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navyBlue/70 via-transparent to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-8 sm:p-12 text-white">
              <h3 className="text-3xl sm:text-4xl font-bold mb-3">Welcoming Students to Excellence Every Day</h3>
              <p className="text-lg sm:text-xl text-white/90 max-w-3xl">
                Our Spanish Colonial architecture honors California's educational heritage while providing modern, purpose-built spaces.
              </p>
            </div>
          </div>

          {/* Aerial View Concept */}
          <div className="relative rounded-3xl overflow-hidden shadow-2xl mb-12 max-h-[400px]">
            <img 
              src={process.env.REACT_APP_CONCEPT_AERIAL_URL} 
              alt="Doctrina Charter School Aerial Campus View" 
              className="w-full h-full object-cover"
              style={{maxHeight: '400px'}}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navyBlue/70 via-transparent to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-8 sm:p-12 text-white">
              <h3 className="text-3xl sm:text-4xl font-bold mb-3">A Comprehensive 38-Acre Campus</h3>
              <p className="text-lg sm:text-xl text-white/90 max-w-3xl">
                Thoughtfully designed layout featuring five distinctive courtyards, athletic facilities, community gardens, and spaces that bring learning to life.
              </p>
            </div>
          </div>
        </div>

        {/* Five Distinctive Learning Courtyards */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-navyBlue mb-4">Five Distinctive Learning Courtyards</h3>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              Each courtyard is named after an influential historical figure, creating unique outdoor learning environments that inspire curiosity and creativity throughout our campus.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
            {courtyards.map((courtyard, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden group">
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={courtyard.image} 
                    alt={courtyard.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navyBlue/80 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h4 className="text-2xl font-bold text-white mb-1">{courtyard.name}</h4>
                  </div>
                </div>
                <CardContent className="p-6">
                  <p className="text-gray-700 mb-3 leading-relaxed">{courtyard.description}</p>
                  <div className={`bg-${courtyard.color}/10 rounded-lg p-3 border-l-4 border-${courtyard.color}`}>
                    <p className="text-sm text-gray-700 italic">{courtyard.purpose}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Designed for 21st Century Learning */}
        <div className="bg-white rounded-2xl p-8 sm:p-12 shadow-lg mb-12">
          <h3 className="text-3xl font-bold text-navyBlue mb-8 text-center">Designed for 21st Century Learning</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-4xl mb-3">🏛️</div>
              <h4 className="font-bold text-navyBlue mb-2">Right-Sized Campus</h4>
              <p className="text-gray-600 text-sm">Optimized for 600 students across grades 6-8, based on educational research</p>
            </div>
            <div className="text-center">
              <div className="text-4xl mb-3">🌳</div>
              <h4 className="font-bold text-navyBlue mb-2">Outdoor Learning Spaces</h4>
              <p className="text-gray-600 text-sm">Five unique courtyards support project-based learning and collaboration</p>
            </div>
            <div className="text-center">
              <div className="text-4xl mb-3">🎨</div>
              <h4 className="font-bold text-navyBlue mb-2">Inspiring Architecture</h4>
              <p className="text-gray-600 text-sm">Beautiful facilities that honor tradition while supporting innovation</p>
            </div>
            <div className="text-center">
              <div className="text-4xl mb-3">🤝</div>
              <h4 className="font-bold text-navyBlue mb-2">Community Building</h4>
              <p className="text-gray-600 text-sm">Unified lunch periods and shared spaces foster school-wide connections</p>
            </div>
            <div className="text-center">
              <div className="text-4xl mb-3">📚</div>
              <h4 className="font-bold text-navyBlue mb-2">Flexible Classrooms</h4>
              <p className="text-gray-600 text-sm">Modern learning environments that adapt to diverse teaching methods</p>
            </div>
            <div className="text-center">
              <div className="text-4xl mb-3">🌟</div>
              <h4 className="font-bold text-navyBlue mb-2">Student-Centered Design</h4>
              <p className="text-gray-600 text-sm">Every space thoughtfully created to support middle school development</p>
            </div>
          </div>
        </div>

        {/* Campus Features */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
            <div className="bg-tealAccent/10 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
              <MapPin className="text-tealAccent" size={28} />
            </div>
            <h4 className="text-xl font-bold text-navyBlue mb-2">38-Acre Campus</h4>
            <p className="text-gray-600">Expansive grounds designed for learning, recreation, and community gathering</p>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
            <div className="bg-sageGreen/10 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
              <Trees className="text-sageGreen" size={28} />
            </div>
            <h4 className="text-xl font-bold text-navyBlue mb-2">Outdoor Spaces</h4>
            <p className="text-gray-600">Five named courtyards and gardens for hands-on learning experiences</p>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
            <div className="bg-goldenOlive/10 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
              <BookOpen className="text-goldenOlive" size={28} />
            </div>
            <h4 className="text-xl font-bold text-navyBlue mb-2">Modern Facilities</h4>
            <p className="text-gray-600">State-of-the-art classrooms, specialized labs, library, and performance spaces</p>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
            <div className="bg-coralRed/10 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
              <Users className="text-coralRed" size={28} />
            </div>
            <h4 className="text-xl font-bold text-navyBlue mb-2">Community Hub</h4>
            <p className="text-gray-600">Extended hours for families, community education programs, and events</p>
          </div>
        </div>

        {/* Athletic Facilities Image */}
        <div className="relative rounded-3xl overflow-hidden shadow-2xl mb-12 max-h-[400px]">
          <img 
            src={process.env.REACT_APP_RUNNING_TRACK_URL} 
            alt="Students running on athletic track" 
            className="w-full h-full object-cover"
            style={{maxHeight: '400px'}}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-navyBlue/70 via-transparent to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-8 sm:p-12 text-white">
            <h3 className="text-3xl sm:text-4xl font-bold mb-3">Athletic Excellence & Physical Well-Being</h3>
            <p className="text-lg sm:text-xl text-white/90 max-w-3xl">
              Our state-of-the-art athletic facilities include a full running track, gymnasium, and sports fields promoting active, healthy lifestyles for all students.
            </p>
          </div>
        </div>

        {/* Additional Campus Info */}
        <div className="bg-gradient-to-br from-tealAccent to-sageGreen rounded-2xl p-8 sm:p-12 text-white">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-3xl font-bold mb-4">Location</h3>
              <p className="text-lg text-white/90 mb-6">
                Ideally located in the Winchester area of California's Inland Empire, serving students from Romoland, Homeland, Menifee, and Winchester school districts.
              </p>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                  <span className="text-white/90">San Bernardino County</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                  <span className="text-white/90">Riverside County</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                  <span className="text-white/90">Accessible to Inland Empire families</span>
                </div>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <h4 className="text-2xl font-bold mb-4">Campus Highlights</h4>
              <ul className="space-y-3">
                <li className="flex items-start space-x-2">
                  <span className="text-navyBlue mt-1">✓</span>
                  <span>Specialized classrooms for arts, music, and STEM</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-navyBlue mt-1">✓</span>
                  <span>Comprehensive special education facilities</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-navyBlue mt-1">✓</span>
                  <span>Gymnasium and athletic facilities</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-navyBlue mt-1">✓</span>
                  <span>Cafeteria and community dining spaces</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-navyBlue mt-1">✓</span>
                  <span>Auditorium for performances and events</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-navyBlue mt-1">✓</span>
                  <span>Spanish Colonial architecture with modern amenities</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CampusVision;